/**
 * Sound effects, synthesized with the Web Audio API — no asset files to ship,
 * host, or hit the artifact CSP over. Every sound is a short oscillator + gain
 * envelope, so the whole SFX set is a few hundred bytes of code.
 *
 * The AudioContext is created lazily on the first play, because browsers block
 * audio until a user gesture. Mute state is persisted to localStorage.
 */

type Voice = { freq: number; type: OscillatorType; dur: number; gain?: number; slideTo?: number };

export type SoundName =
  | 'tick' | 'solve' | 'wrong' | 'oppFinish' | 'beep' | 'go' | 'win' | 'lose'
  | 'emoteYou' | 'emoteRival';

const SOUNDS: Record<SoundName, Voice> = {
  /** Soft tick, for the clock in its final seconds. */
  tick: { freq: 880, type: 'sine', dur: 0.04, gain: 0.12 },
  /** You solved the minigame. */
  solve: { freq: 660, type: 'triangle', dur: 0.18, gain: 0.22, slideTo: 990 },
  /** A wrong move / miss. */
  wrong: { freq: 200, type: 'sawtooth', dur: 0.12, gain: 0.16, slideTo: 120 },
  /** Opponent finished — a sharp warning sting. */
  oppFinish: { freq: 520, type: 'square', dur: 0.1, gain: 0.16, slideTo: 300 },
  /** Countdown beep (3-2-1). */
  beep: { freq: 440, type: 'sine', dur: 0.09, gain: 0.18 },
  /** Countdown GO / race start. */
  go: { freq: 550, type: 'triangle', dur: 0.22, gain: 0.24, slideTo: 880 },
  /** Match won. */
  win: { freq: 523, type: 'triangle', dur: 0.5, gain: 0.25, slideTo: 1047 },
  /** Match lost. */
  lose: { freq: 330, type: 'sine', dur: 0.5, gain: 0.2, slideTo: 165 },
  /** Your emote lands — a bright upward pop. */
  emoteYou: { freq: 700, type: 'triangle', dur: 0.1, gain: 0.16, slideTo: 1100 },
  /** Rival's emote lands — a softer, lower blip. */
  emoteRival: { freq: 500, type: 'sine', dur: 0.1, gain: 0.12, slideTo: 760 },
};

const STORAGE_KEY = 'mgr:muted';

let ctx: AudioContext | null = null;
let muted = readMuted();

function readMuted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(next: boolean): void {
  muted = next;
  try {
    localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  } catch {
    /* private mode / no storage — in-memory mute still works */
  }
}

function ensureContext(): AudioContext | null {
  if (muted) return null;
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  // A context created before a gesture starts 'suspended'.
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function play(name: SoundName): void {
  const audio = ensureContext();
  if (!audio) return;
  const v = SOUNDS[name];
  const now = audio.currentTime;

  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = v.type;
  osc.frequency.setValueAtTime(v.freq, now);
  if (v.slideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(v.slideTo, now + v.dur);

  const peak = v.gain ?? 0.2;
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(peak, now + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + v.dur);

  osc.connect(gain).connect(audio.destination);
  osc.start(now);
  osc.stop(now + v.dur + 0.02);
}
